import { useState, useEffect, useCallback, useRef } from 'react';
import { SubscriptionData, SubscriptionStatusResponse } from '../types/subscription';
import { apiService } from '../services/api';
import { persistence } from '../services/persistence';

const SUBSCRIPTION_STORAGE_KEY = 'eir-subscription-status';
const SUBSCRIPTION_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes cache

// Module-level flag to prevent multiple simultaneous loads
let isLoadingSubscription = false;
let loadPromise: Promise<void> | null = null;

/**
 * Hook for managing subscription state
 * 
 * Features:
 * - Caches subscription status in localStorage
 * - Automatically refreshes on mount
 * - Provides refresh function for manual updates
 * - Handles email extraction from user profile
 */
export const useSubscription = () => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get user email from localStorage profile
   */
  const getUserEmail = useCallback((): string | null => {
    const profile = persistence.getJSON<{ email?: string } | null>('eir-user-profile', null);
    return profile?.email || null;
  }, []);

  /**
   * Load subscription status from cache or API
   * Uses module-level singleton to prevent multiple simultaneous loads
   */
  const loadSubscription = useCallback(async () => {
    // Check cache first - this avoids API calls entirely if cache is valid
    const cachedEntry = persistence.getJSON<{ data: SubscriptionData; timestamp: number } | null>(SUBSCRIPTION_STORAGE_KEY, null);
    if (cachedEntry && Date.now() - cachedEntry.timestamp < SUBSCRIPTION_EXPIRY_MS) {
      setSubscription(cachedEntry.data);
      setLoading(false);
      return;
    }

    // If already loading, wait for existing load to complete
    if (isLoadingSubscription && loadPromise) {
      await loadPromise;
      // After waiting, check cache again (other instance might have updated it)
      const updatedCache = persistence.getJSON<{ data: SubscriptionData } | null>(SUBSCRIPTION_STORAGE_KEY, null);
      if (updatedCache) {
        setSubscription(updatedCache.data);
        setLoading(false);
        return;
      }
    }

    // Start loading (only if not already loading)
    if (!isLoadingSubscription) {
      isLoadingSubscription = true;
      setLoading(true);
      setError(null);

      loadPromise = (async () => {
        try {
          // Get email from profile
          const email = getUserEmail();
          if (!email) {
            setSubscription(null);
            return;
          }

          // Fetch from backend
          const status: SubscriptionStatusResponse = await apiService.getSubscriptionStatus(email);
          
          const subscriptionData: SubscriptionData = {
            active: status.active,
            plan: status.plan,
            expiresAt: status.expiresAt,
            status: status.status,
            cancelAtPeriodEnd: status.cancelAtPeriodEnd,
            email,
          };

          setSubscription(subscriptionData);

          // Cache result
          persistence.setJSON(SUBSCRIPTION_STORAGE_KEY, {
            data: subscriptionData,
            timestamp: Date.now(),
          });
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load subscription';
          console.error('Error loading subscription:', errorMessage);
          setError(errorMessage);
          
          // On error, try to use cached data if available
          const cached = persistence.getJSON<{ data: SubscriptionData } | null>(SUBSCRIPTION_STORAGE_KEY, null);
          if (cached) {
            setSubscription(cached.data);
          } else {
            setSubscription(null);
          }
        } finally {
          isLoadingSubscription = false;
          loadPromise = null;
          setLoading(false);
        }
      })();

      await loadPromise;
    } else {
      // Another instance is loading, just wait and use cache
      setLoading(true);
      try {
        await loadPromise;
        const finalCache = persistence.getJSON<{ data: SubscriptionData } | null>(SUBSCRIPTION_STORAGE_KEY, null);
        if (finalCache) {
          setSubscription(finalCache.data);
        }
      } finally {
        setLoading(false);
      }
    }
  }, [getUserEmail]);

  /**
   * Refresh subscription status (clears cache and refetches)
   */
  const refreshSubscription = useCallback(async () => {
    console.log('[useSubscription] Refreshing subscription status...');
    persistence.removeItem(SUBSCRIPTION_STORAGE_KEY);
    // Reset module-level loading flag to force fresh API call
    isLoadingSubscription = false;
    loadPromise = null;
    
    setLoading(true);
    setError(null);

    try {
      // Get email from profile
      const email = getUserEmail();
      console.log('[useSubscription] Email from profile:', email);
      
      if (!email) {
        console.log('[useSubscription] No email found in profile');
        setSubscription(null);
        setLoading(false);
        return;
      }

      console.log('[useSubscription] Fetching subscription status from API...');
      // Fetch from backend (bypass cache by calling API directly)
      const status: SubscriptionStatusResponse = await apiService.getSubscriptionStatus(email);
      
      console.log('[useSubscription] API response:', status);
      
      const subscriptionData: SubscriptionData = {
        active: status.active,
        plan: status.plan,
        expiresAt: status.expiresAt,
        status: status.status,
        cancelAtPeriodEnd: status.cancelAtPeriodEnd,
        email,
      };

      setSubscription(subscriptionData);

      // Cache result
      persistence.setJSON(SUBSCRIPTION_STORAGE_KEY, {
        data: subscriptionData,
        timestamp: Date.now(),
      });
      
      console.log('[useSubscription] Subscription status updated:', subscriptionData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load subscription';
      console.error('[useSubscription] Error loading subscription:', errorMessage);
      setError(errorMessage);
      
      // On error, try to use cached data if available
      const cached = persistence.getJSON<{ data: SubscriptionData } | null>(SUBSCRIPTION_STORAGE_KEY, null);
      if (cached) {
        setSubscription(cached.data);
      } else {
        setSubscription(null);
      }
    } finally {
      setLoading(false);
    }
  }, [getUserEmail]);

  /**
   * Check if user has active premium subscription
   */
  const isPremium = subscription?.active === true && subscription?.status === 'active';

  // Track last email checked to prevent unnecessary API calls
  const lastEmailRef = useRef<string | null>(null);
  const hasLoadedRef = useRef(false);

  // Load subscription on mount only (once)
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadSubscription();
      // Set initial email
      lastEmailRef.current = getUserEmail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - intentionally empty

  // Listen for storage events and profile updates (debounced)
  useEffect(() => {
    let debounceTimer: NodeJS.Timeout | null = null;

    const checkEmailAndRefresh = () => {
      const currentEmail = getUserEmail();
      
      // Only refresh if email actually changed (not just while typing)
      if (currentEmail !== lastEmailRef.current && currentEmail) {
        // Debounce: wait 2 seconds after last change before checking
        if (debounceTimer) {
          clearTimeout(debounceTimer);
        }
        
        debounceTimer = setTimeout(() => {
          lastEmailRef.current = currentEmail;
          refreshSubscription();
        }, 2000); // Wait 2 seconds after user stops typing/changing email
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SUBSCRIPTION_STORAGE_KEY || e.key === 'eir-user-profile') {
        if (e.key === 'eir-user-profile') {
          // Profile changed - debounce the refresh
          checkEmailAndRefresh();
        } else {
          // Subscription cache changed - refresh immediately
          loadSubscription();
        }
      }
    };

    // Listen for custom 'profileUpdated' event for same-tab updates
    const handleProfileUpdate = () => {
      checkEmailAndRefresh();
    };

    // Poll for email changes (only if no email set yet)
    const pollInterval = setInterval(() => {
      const currentEmail = getUserEmail();
      if (!lastEmailRef.current && currentEmail) {
        // Email was just added - refresh once
        lastEmailRef.current = currentEmail;
        refreshSubscription();
      }
    }, 5000); // Check every 5 seconds if email is missing

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('profileUpdated', handleProfileUpdate);
    
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      clearInterval(pollInterval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, [loadSubscription, refreshSubscription, getUserEmail]);

  return {
    subscription,
    loading,
    error,
    isPremium,
    refreshSubscription,
    getUserEmail,
  };
};

